import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AppIcon from '../../components/AppIcon';
import { detectUserCountry, getRegionalPricing } from '../../services/geolocationService';
import { authService } from '../../services/authService';
import ErrorRecoveryModal from './components/ErrorRecoveryModal';

const AcademyRegistration = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [regionalPricing, setRegionalPricing] = useState({ amount: 99, currency: 'USD', symbol: '$', formatted: '$99' });
  const [userCountry, setUserCountry] = useState('US');
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [showErrorRecovery, setShowErrorRecovery] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [registrationAttempts, setRegistrationAttempts] = useState(0);
  const [formData, setFormData] = useState({
    // Academy Information
    academyName: '',
    subdomain: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    phone: '',
    website: '',
    
    // Owner Information
    ownerFirstName: '',
    ownerLastName: '',
    ownerEmail: '',
    ownerPhone: '',
    password: '',
    confirmPassword: '',
    
    // Billing Information
    estimatedStudents: 25,
    averageMonthlyFee: 150,
    
    // Terms
    acceptTerms: false,
    acceptPrivacy: false
  });

  // Detect user location and set regional pricing
  useEffect(() => {
    const initializeLocalization = async () => {
      try {
        setIsLoadingLocation(true);
        const geoData = await detectUserCountry();
        
        // Set language based on detected country
        if (geoData?.language && i18n?.language !== geoData?.language) {
          await i18n?.changeLanguage(geoData?.language);
        }
        
        // Set pricing based on detected country
        const pricing = getRegionalPricing(geoData?.country);
        setRegionalPricing(pricing);
        setUserCountry(geoData?.country);
        
        // Update form data with detected country
        setFormData(prev => ({
          ...prev,
          country: geoData?.country || 'US'
        }));
        
      } catch (error) {
        console.error('Failed to initialize localization:', error);
        // Keep default USD values
      } finally {
        setIsLoadingLocation(false);
      }
    };

    initializeLocalization();
  }, [i18n]);

  const steps = [
    { id: 1, name: t('registration.steps.academyInfo'), description: t('registration.steps.academyInfoDesc') },
    { id: 2, name: t('registration.steps.ownerDetails'), description: t('registration.steps.ownerDetailsDesc') },
    { id: 3, name: t('registration.steps.complete'), description: t('registration.steps.completeDesc') },
  ];

  // Calculate pricing preview with regional currency
  const calculateMonthlyFee = () => {
    const baseRate = regionalPricing?.amount; // Use regional base rate (R$50 for BR, $99 for US)
    const students = parseInt(formData?.estimatedStudents) || 0;
    const monthlyFee = parseFloat(formData?.averageMonthlyFee) || 0;
    
    let studentFees = 0;
    if (students <= 50) {
      studentFees = students * monthlyFee * 0.01; // 1%
    } else if (students <= 101) {
      studentFees = (50 * monthlyFee * 0.01) + ((students - 50) * monthlyFee * 0.02); // 1% + 2%
    } else {
      studentFees = (50 * monthlyFee * 0.01) + (51 * monthlyFee * 0.02) + ((students - 101) * monthlyFee * 0.05); // 1% + 2% + 5%
    }
    
    return {
      baseRate,
      studentFees,
      total: baseRate + studentFees,
      currency: regionalPricing?.currency,
      symbol: regionalPricing?.symbol
    };
  };

  const generateSubdomain = (academyName) => {
    return academyName?.toLowerCase()?.replace(/[^a-zA-Z0-9]/g, '')?.substring(0, 20);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate subdomain from academy name
      if (field === 'academyName' && value) {
        updated.subdomain = generateSubdomain(value);
      }
      
      return updated;
    });
    setError(''); // Clear errors when user types
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData?.academyName?.trim()) return t('registration.errors.academyNameRequired');
        if (!formData?.subdomain?.trim()) return t('registration.errors.subdomainRequired');
        if (!formData?.city?.trim()) return t('registration.errors.cityRequired');
        if (!formData?.state?.trim()) return t('registration.errors.stateRequired');
        break;
      case 2:
        if (!formData?.ownerFirstName?.trim()) return t('registration.errors.firstNameRequired');
        if (!formData?.ownerLastName?.trim()) return t('registration.errors.lastNameRequired');
        if (!formData?.ownerEmail?.trim()) return t('registration.errors.emailRequired');
        if (!/\S+@\S+\.\S+/?.test(formData?.ownerEmail)) return t('registration.errors.emailInvalid');
        if (!formData?.password || formData?.password?.length < 6) return t('registration.errors.passwordRequired');
        if (formData?.password !== formData?.confirmPassword) return t('registration.errors.passwordMismatch');
        break;
      case 3:
        if (!formData?.acceptTerms) return t('registration.errors.termsRequired');
        if (!formData?.acceptPrivacy) return t('registration.errors.privacyRequired');
        break;
      default:
        return null;
    }
    return null;
  };

  const handleNext = () => {
    const error = validateStep(currentStep);
    if (error) {
      setError(error);
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    setError('');
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const checkSubdomainAvailability = async (subdomain) => {
    if (!subdomain) return false;
    
    const { data, error } = await supabase?.from('organizations')?.select('id')?.eq('subdomain', subdomain)?.limit(1);
    
    return !error && (!data || data?.length === 0);
  };

  const handleRegister = async () => {
    const error = validateStep(3);
    if (error) {
      setError(error);
      return;
    }

    setLoading(true);
    setError('');
    setRegistrationAttempts(prev => prev + 1);

    try {
      // Use enhanced registration service
      const result = await authService?.registerAcademy({
        academyName: formData?.academyName,
        subdomain: formData?.subdomain,
        ownerFirstName: formData?.ownerFirstName,
        ownerLastName: formData?.ownerLastName,
        ownerEmail: formData?.ownerEmail,
        ownerPhone: formData?.ownerPhone,
        password: formData?.password,
        address: formData?.address,
        city: formData?.city,
        state: formData?.state,
        zipCode: formData?.zipCode,
        country: formData?.country,
        phone: formData?.phone,
        website: formData?.website,
        estimatedStudents: formData?.estimatedStudents,
        averageMonthlyFee: formData?.averageMonthlyFee
      });

      if (result?.success) {
        // Success! Navigate to success page
        navigate('/academy-creation-success', {
          state: {
            message: 'Welcome to MyDojo! Your 60-day trial has started.',
            academyName: formData?.academyName,
            subdomain: formData?.subdomain,
            userId: result?.userId,
            organizationId: result?.organizationId
          }
        });
        return;
      }

      // Handle specific error types
      const errorType = result?.errorType || 'UNKNOWN_ERROR';
      
      if (errorType === 'PROFILE_TIMEOUT') {
        setErrorDetails({
          type: 'PROFILE_TIMEOUT',
          title: 'Account Setup Taking Longer Than Expected',
          message: 'Your account was created successfully, but setup is taking longer than usual. This is normal during high traffic.',
          technicalDetails: result?.error || 'User profile creation trigger did not complete within expected timeframe.',
          canRetry: true,
          retryDelay: 3000,
          attempts: registrationAttempts,
          userId: result?.userId,
          fallbackAction: {
            label: 'Try Logging In',
            action: () => navigate('/login', { state: { email: formData?.ownerEmail } })
          },
          suggestions: [
            'Wait 30 seconds and click Retry',
            'Check your email for confirmation link',
            'Try logging in directly if you received confirmation email'
          ]
        });
        setShowErrorRecovery(true);
      } else if (errorType === 'SUBDOMAIN_TAKEN') {
        setErrorDetails({
          type: 'SUBDOMAIN_TAKEN',
          title: 'Subdomain Already Taken',
          message: `The subdomain "${formData?.subdomain}" is already in use. Please choose a different subdomain.`,
          technicalDetails: result?.error,
          canRetry: false,
          fallbackAction: {
            label: 'Change Subdomain',
            action: () => {
              setShowErrorRecovery(false);
              setCurrentStep(1);
            }
          },
          suggestions: [
            'Try adding your city name to the subdomain',
            'Use your full academy name',
            'Add numbers or abbreviations'
          ]
        });
        setShowErrorRecovery(true);
      } else if (errorType === 'ORG_CREATION_ERROR') {
        setErrorDetails({
          type: 'RLS_POLICY',
          title: 'Organization Setup Issue',
          message: 'There was a temporary issue creating your academy workspace. This usually resolves automatically.',
          technicalDetails: result?.error || 'Organization creation failed due to database permissions.',
          canRetry: result?.canRetry !== false,
          retryDelay: 2000,
          attempts: registrationAttempts,
          userId: result?.userId,
          suggestions: [
            'Click Retry to attempt organization creation again',
            'The system will validate your profile is ready',
            'Contact support if issue persists after 3 attempts'
          ]
        });
        setShowErrorRecovery(true);
      } else if (errorType === 'AUTH_ERROR') {
        const isDuplicate = result?.error?.toLowerCase()?.includes('already') || 
                           result?.error?.toLowerCase()?.includes('exists');
        
        if (isDuplicate) {
          setErrorDetails({
            type: 'DUPLICATE_ACCOUNT',
            title: 'Account Already Exists',
            message: 'An account with this email already exists. Please try logging in instead.',
            technicalDetails: result?.error,
            canRetry: false,
            fallbackAction: {
              label: 'Go to Login',
              action: () => navigate('/login', { state: { email: formData?.ownerEmail } })
            },
            suggestions: [
              'Use the login page with your existing credentials',
              'Reset your password if you forgot it',
              'Use a different email address to create a new account'
            ]
          });
        } else {
          setErrorDetails({
            type: 'GENERAL_ERROR',
            title: 'Account Creation Failed',
            message: result?.error || 'Failed to create your account. Please check your information and try again.',
            technicalDetails: result?.error,
            canRetry: true,
            retryDelay: 1000,
            attempts: registrationAttempts,
            suggestions: [
              'Check your email format is correct',
              'Ensure password meets requirements (6+ characters)',
              'Try again in a few moments'
            ]
          });
        }
        setShowErrorRecovery(true);
      } else {
        setErrorDetails({
          type: 'GENERAL_ERROR',
          title: 'Registration Failed',
          message: result?.error || 'An unexpected error occurred during registration. Please try again.',
          technicalDetails: result?.error || 'Unknown error',
          canRetry: result?.canRetry !== false,
          retryDelay: 1000,
          attempts: registrationAttempts,
          suggestions: [
            'Check your internet connection',
            'Verify all required fields are filled',
            'Try again in a few moments'
          ]
        });
        setShowErrorRecovery(true);
      }

      setError(result?.error || t('registration.errors.registrationFailed'));

    } catch (err) {
      console.error('Registration error:', err);
      
      setErrorDetails({
        type: 'GENERAL_ERROR',
        title: 'Unexpected Error',
        message: 'An unexpected error occurred. Please try again.',
        technicalDetails: err?.message || 'Unknown error',
        canRetry: true,
        retryDelay: 1000,
        attempts: registrationAttempts,
        suggestions: [
          'Check your internet connection',
          'Refresh the page and try again',
          'Contact support if issue persists'
        ]
      });
      setShowErrorRecovery(true);
      setError(err?.message || t('registration.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Enhanced retry handler
  const handleRetry = async () => {
    setShowErrorRecovery(false);
    
    // Wait for retry delay if specified
    if (errorDetails?.retryDelay) {
      await new Promise(resolve => setTimeout(resolve, errorDetails.retryDelay));
    }

    // If we have a userId, try to retry organization creation only
    if (errorDetails?.userId && errorDetails?.type !== 'AUTH_ERROR') {
      setLoading(true);
      setError('');

      try {
        const result = await authService?.retryOrganizationCreation(
          errorDetails?.userId,
          {
            academyName: formData?.academyName,
            subdomain: formData?.subdomain,
            address: formData?.address,
            city: formData?.city,
            state: formData?.state,
            zipCode: formData?.zipCode,
            country: formData?.country,
            phone: formData?.phone,
            website: formData?.website,
            estimatedStudents: formData?.estimatedStudents,
            averageMonthlyFee: formData?.averageMonthlyFee
          }
        );

        if (result?.success) {
          navigate('/academy-creation-success', {
            state: {
              message: 'Welcome to MyDojo! Your 60-day trial has started.',
              academyName: formData?.academyName,
              subdomain: formData?.subdomain,
              userId: errorDetails?.userId,
              organizationId: result?.organizationId
            }
          });
          return;
        }

        // Retry failed, show error again
        setError(result?.error || 'Retry failed');
        setErrorDetails(prev => ({
          ...prev,
          attempts: (prev?.attempts || 0) + 1,
          technicalDetails: result?.error || prev?.technicalDetails
        }));
        setShowErrorRecovery(true);

      } catch (err) {
        console.error('Retry error:', err);
        setError(err?.message || 'Retry failed');
        setShowErrorRecovery(true);
      } finally {
        setLoading(false);
      }
    } else {
      // Full retry - start registration from beginning
      await handleRegister();
    }
  };

  const pricing = calculateMonthlyFee();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center space-x-3">
            <img src="/assets/images/big_logo_black-1767417875469.png" alt="MyDojo Logo" className="h-12" />
          </div>
          <p className="text-center text-gray-600 mt-2">
            {t('registration.header.title')}
          </p>
        </div>
      </div>
      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="relative flex items-center justify-between mb-12">
          {steps?.map((step, index) => (
            <React.Fragment key={step?.id}>
              <div className="flex flex-col items-center flex-1 relative z-10">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl transition-all duration-300 ${
                    currentStep > step?.id
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg ring-4 ring-green-100'
                      : currentStep === step?.id
                      ? 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-xl ring-4 ring-red-100 scale-110'
                      : 'bg-white border-2 border-gray-300 text-gray-400 shadow-sm'
                  }`}
                >
                  {currentStep > step?.id ? (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step?.id
                  )}
                </div>
                <div className="text-center mt-3 max-w-[120px]">
                  <div
                    className={`text-sm font-semibold transition-colors ${
                      currentStep >= step?.id ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {step?.name}
                  </div>
                  <div className={`text-xs mt-1 transition-colors ${
                    currentStep >= step?.id ? 'text-gray-600' : 'text-gray-400'
                  }`}>
                    {step?.description}
                  </div>
                </div>
              </div>
              {index < steps?.length - 1 && (
                <div className="flex-1 relative" style={{ maxWidth: '140px', marginBottom: '60px' }}>
                  <div className="h-1 bg-gray-200 rounded-full">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        currentStep > step?.id
                          ? 'bg-gradient-to-r from-green-500 to-green-600 w-full'
                          : currentStep === step?.id
                          ? 'bg-gradient-to-r from-red-600 to-red-700 w-1/2' :'w-0'
                      }`}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left Column - Form */}
            <div className="p-8 border-r border-gray-200">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Step 1: Academy Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('registration.step1.title')}</h2>
                    <p className="text-gray-600">{t('registration.step1.subtitle')}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Input
                        label={`${t('registration.step1.academyName')} *`}
                        value={formData?.academyName}
                        onChange={(e) => handleInputChange('academyName', e?.target?.value)}
                        placeholder={t('registration.step1.academyNamePlaceholder')}
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration.step1.subdomain')} *
                        </label>
                        <div className="flex items-center">
                          <Input
                            value={formData?.subdomain}
                            onChange={(e) => handleInputChange('subdomain', e?.target?.value)}
                            placeholder={t('registration.step1.subdomainPlaceholder')}
                            required
                            className="rounded-r-none"
                          />
                          <span className="bg-gray-100 border border-l-0 border-gray-300 px-3 py-2 text-sm text-gray-500 rounded-r-md">
                            .mydojo.info
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('registration.step1.subdomainHelper')}
                        </p>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <Input
                        label={t('registration.step1.address')}
                        value={formData?.address}
                        onChange={(e) => handleInputChange('address', e?.target?.value)}
                        placeholder={t('registration.step1.addressPlaceholder')}
                      />
                    </div>

                    <Input
                      label={`${t('registration.step1.city')} *`}
                      value={formData?.city}
                      onChange={(e) => handleInputChange('city', e?.target?.value)}
                      placeholder={t('registration.step1.cityPlaceholder')}
                      required
                    />

                    <Input
                      label={`${t('registration.step1.state')} *`}
                      value={formData?.state}
                      onChange={(e) => handleInputChange('state', e?.target?.value)}
                      placeholder={t('registration.step1.statePlaceholder')}
                      required
                    />

                    <Input
                      label={t('registration.step1.zipCode')}
                      value={formData?.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                      placeholder={t('registration.step1.zipCodePlaceholder')}
                    />

                    <Input
                      label={t('registration.step1.phone')}
                      value={formData?.phone}
                      onChange={(e) => handleInputChange('phone', e?.target?.value)}
                      placeholder={t('registration.step1.phonePlaceholder')}
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Owner Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('registration.step2.title')}</h2>
                    <p className="text-gray-600">{t('registration.step2.subtitle')}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label={`${t('registration.step2.firstName')} *`}
                      value={formData?.ownerFirstName}
                      onChange={(e) => handleInputChange('ownerFirstName', e?.target?.value)}
                      placeholder={t('registration.step2.firstNamePlaceholder')}
                      required
                    />

                    <Input
                      label={`${t('registration.step2.lastName')} *`}
                      value={formData?.ownerLastName}
                      onChange={(e) => handleInputChange('ownerLastName', e?.target?.value)}
                      placeholder={t('registration.step2.lastNamePlaceholder')}
                      required
                    />

                    <div className="md:col-span-2">
                      <Input
                        type="email"
                        label={`${t('registration.step2.email')} *`}
                        value={formData?.ownerEmail}
                        onChange={(e) => handleInputChange('ownerEmail', e?.target?.value)}
                        placeholder={t('registration.step2.emailPlaceholder')}
                        required
                      />
                    </div>

                    <Input
                      label={t('registration.step2.ownerPhone')}
                      value={formData?.ownerPhone}
                      onChange={(e) => handleInputChange('ownerPhone', e?.target?.value)}
                      placeholder={t('registration.step2.ownerPhonePlaceholder')}
                    />

                    <div />

                    <Input
                      type="password"
                      label={`${t('registration.step2.password')} *`}
                      value={formData?.password}
                      onChange={(e) => handleInputChange('password', e?.target?.value)}
                      placeholder={t('registration.step2.passwordPlaceholder')}
                      required
                    />

                    <Input
                      type="password"
                      label={`${t('registration.step2.confirmPassword')} *`}
                      value={formData?.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                      placeholder={t('registration.step2.confirmPasswordPlaceholder')}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Complete */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('registration.step4.title')}</h2>
                    <p className="text-gray-600">{t('registration.step4.subtitle')}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Registration Summary</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <div>
                        <dt className="text-gray-500">{t('registration.step1.academyName')}</dt>
                        <dd className="text-gray-900 font-medium">{formData?.academyName}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('registration.step1.subdomain')}</dt>
                        <dd className="text-gray-900 font-medium">{formData?.subdomain}.mydojo.info</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Owner</dt>
                        <dd className="text-gray-900 font-medium">
                          {formData?.ownerFirstName} {formData?.ownerLastName}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('registration.step2.email')}</dt>
                        <dd className="text-gray-900 font-medium">{formData?.ownerEmail}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t('registration.step3.estimatedStudents')}</dt>
                        <dd className="text-gray-900 font-medium">{formData?.estimatedStudents}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Trial Ends</dt>
                        <dd className="text-gray-900 font-medium">
                          {new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)?.toLocaleDateString()}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-start space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData?.acceptTerms}
                        onChange={(e) => handleInputChange('acceptTerms', e?.target?.checked)}
                        className="mt-1 w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {t('registration.step4.acceptTerms')}
                      </span>
                    </label>

                    <label className="flex items-start space-x-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData?.acceptPrivacy}
                        onChange={(e) => handleInputChange('acceptPrivacy', e?.target?.checked)}
                        className="mt-1 w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {t('registration.step4.acceptPrivacy')}
                      </span>
                    </label>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AppIcon name="CheckCircle" className="w-5 h-5 text-green-600 mr-2" />
                      <span className="text-green-700 font-medium">{t('registration.step3.trialNote')}</span>
                    </div>
                    <p className="text-green-600 text-sm mt-1">
                      {t('registration.step4.finalNote')}
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
                {currentStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    {t('registration.buttons.back')}
                  </Button>
                ) : (
                  <div />
                )}

                {currentStep < steps?.length ? (
                  <Button
                    onClick={handleNext}
                    disabled={loading}
                  >
                    {t('registration.buttons.next')}
                  </Button>
                ) : (
                  <Button
                    onClick={handleRegister}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 relative"
                  >
                    {loading ? (
                      <span className="flex items-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{t('registration.step4.completing')}</span>
                      </span>
                    ) : (
                      t('registration.step4.completeButton')
                    )}
                  </Button>
                )}
              </div>

              {/* Loading Progress Indicator */}
              {loading && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="animate-spin h-5 w-5 text-blue-600 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div className="flex-1">
                      <p className="text-blue-900 font-medium">Creating your academy...</p>
                      <p className="text-blue-700 text-sm mt-1">This may take up to 15 seconds. Please don't close this window.</p>
                      <div className="mt-3 space-y-2 text-xs text-blue-600">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                          <span>Setting up your account</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                          <span>Configuring database permissions</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                          <span>Creating your academy workspace</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Pricing Info */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-8">
              <div className="sticky top-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Pricing Calculator</h3>
                
                {isLoadingLocation ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Fixed Pricing Display */}
                    <div className="bg-white rounded-lg border-2 border-red-600 p-6 shadow-md">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-600 mb-2">Monthly Subscription</div>
                        <div className="text-4xl font-bold text-red-600 mb-1">
                          {regionalPricing?.symbol}{regionalPricing?.amount}
                        </div>
                        <div className="text-sm text-gray-500">{regionalPricing?.currency} - {userCountry === 'BR' ? 'Brazil' : 'United States'}</div>
                      </div>
                    </div>

                    {/* Trial Benefits */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>60-day free trial</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>No setup fees</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Cancel anytime</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-700">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>+{regionalPricing?.currency === 'BRL' ? 'R$5' : '$10'} adjustment every 2 years</span>
                      </div>
                    </div>

                    {/* What's Included */}
                    <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-3">What's Included</h4>
                      <div className="space-y-2">
                        {[
                          'Student management system',
                          'Attendance tracking',
                          'Belt progression tracking',
                          'Payment processing',
                          'Communication tools',
                          'Financial reporting',
                          'Mobile app access',
                          '24/7 customer support'
                        ]?.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm text-blue-800">
                            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            
            <div className="flex justify-between items-center">
              <button
                onClick={handleBack}
                disabled={currentStep === 1 || loading}
                className="px-6 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('registration.buttons.back')}
              </button>
              
              <button
                onClick={handleNext}
                disabled={loading}
                className="px-8 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>{t('registration.step4.completing')}</span>
                  </span>
                ) : currentStep === 3 ? (
                  t('registration.step4.completeButton')
                ) : (
                  t('registration.buttons.next')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Error Recovery Modal */}
      <ErrorRecoveryModal
        isOpen={showErrorRecovery}
        onClose={() => {
          setShowErrorRecovery(false);
          setErrorDetails(null);
        }}
        errorDetails={errorDetails}
        onRetry={handleRetry}
        isRetrying={loading}
      />
    </div>
  );
};

export default AcademyRegistration;